/**
 * Dashboard Page
 * Shows project overview and quick actions
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "@/components/ui/link";
import { useTranslations } from "@/hooks/use-translations";
import {
	FolderKanban,
	ArrowRight,
	Plus,
	BarChart3,
} from "lucide-react";
import { motion } from "motion/react";
import { useActiveTeam } from "@/hooks/use-team";
import { useAuth } from "@/hooks/use-auth";
import { CreateProjectModal } from "@/components/create-project-modal";
import { Button } from "@/components/ui/button";

export function DashboardPage() {
	const t = useTranslations("dashboard");
	const { activeTeam } = useActiveTeam();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [showCreateModal, setShowCreateModal] = useState(false);

	const handleProjectCreated = (chatId: string) => {
		navigate(`/workspace/${chatId}`);
	};

	// "Create Project" opens the name modal below instead of the old brief
	// form, then goes straight to the chat — no href, so the render below
	// treats it as a button.
	const quickActions = [
		{
			key: "createProject",
			label: t("actions.createProject"),
			description: t("actions.createProjectDesc"),
			icon: Plus,
			onClick: () => setShowCreateModal(true),
		},
		{
			key: "viewProjects",
			href: "/projects",
			label: t("actions.viewProjects"),
			description: t("actions.viewProjectsDesc"),
			icon: FolderKanban,
		},
	];

	return (
		<div className="min-h-screen rounded-2xl bg-bg-canvas">
			{/* Header */}
			<div className="border-b border-border-subtle rounded-t-2xl bg-bg-surface">
				<div className="max-w-7xl mx-auto px-6 py-4 sm:px-6 lg:px-8">
					<motion.div
						className="flex items-center justify-between"
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.2 }}
					>
						<div>
							<h1 className="text-2xl font-semibold text-text-primary">
								{t("welcome")}
							</h1>
							<p className="mt-1 text-sm text-text-tertiary">{t("subtitle")}</p>
						</div>
						<Button type="button" onClick={() => setShowCreateModal(true)}>
							<Plus size={16} />
							{t("actions.createProject")}
						</Button>
					</motion.div>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-6 py-8 sm:px-6 lg:px-8">
				{/* Quick Actions Grid */}
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.2, delay: 0.1 }}
				>
					<h2 className="text-lg font-semibold text-text-primary mb-4">
						{t("quickActions")}
					</h2>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
						{quickActions.map((action, i) => {
							const Icon = action.icon;
							const cardContent = (
								<div className="h-full rounded-2xl border border-border-subtle bg-bg-surface hover:border-brand-700 hover:bg-brand-50 transition-all hover:shadow-lg">
									<div className="p-5">
										<div className="flex items-start gap-3">
											<div className="p-2 rounded-lg bg-bg-canvas group-hover:bg-brand-100 transition-colors">
												<Icon
													size={18}
													className="text-text-primary group-hover:text-text-primary transition-colors"
												/>
											</div>
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium text-text-primary group-hover:text-text-primary transition-colors">
													{action.label}
												</p>
												<p className="text-xs text-text-tertiary mt-0.5">
													{action.description}
												</p>
											</div>
											<ArrowRight
												size={14}
												className="text-text-tertiary group-hover:text-text-primary transition-colors flex-shrink-0"
											/>
										</div>
									</div>
								</div>
							);
							return (
								<motion.div
									key={action.key}
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.2, delay: 0.15 + i * 0.03 }}
								>
									{action.onClick ? (
										<button
											type="button"
											onClick={action.onClick}
											className="group w-full text-left"
										>
											{cardContent}
										</button>
									) : (
										<Link href={action.href!} className="group">
											{cardContent}
										</Link>
									)}
								</motion.div>
							);
						})}
					</div>
				</motion.div>
			</div>

			<CreateProjectModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				onCreated={handleProjectCreated}
			/>
		</div>
	);
}
